// @flow

import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { BUTTON_KEY_NAMES } from '@alphaott/smart-tv-platforms'

import {
  changeForceShowControls,
  changeShowControls,
  changeShowSubtitlesSettings,
  changeShowTVListControl,
} from '../models/actions'
import {
  getForceShowControls,
  getShowControls,
  getShowSubtitlesSettings,
  getShowListControl,
} from '../models/selectors'
import { usePlayerContext, RECOVERY_PHASES } from '../store'

const HIDE_CONTROLS_DELAY = 5000

export const useUiControls = ({ entity }) => {
  const dispatch = useDispatch()

  const isForceShowControls = useSelector(getForceShowControls)
  const isShowControls = useSelector(getShowControls)
  const isShowSubtitlesSettings = useSelector(getShowSubtitlesSettings)
  const isShowListControl = useSelector(getShowListControl)

  const ref = useRef(null)

  // Arrows belong to the playback error modal while it is open
  const [playerState] = usePlayerContext()
  const isRecoveryFailed = playerState.recovery === RECOVERY_PHASES.FAILED

  const handleForceShowControls = useCallback(
    (value) => {
      dispatch(changeForceShowControls(value))
    },
    [dispatch],
  )

  const handleSetTimeout = useCallback(() => {
    ref.current = setTimeout(() => {
      dispatch(changeShowControls(false))
    }, HIDE_CONTROLS_DELAY)
  }, [dispatch])

  const handleClearTimeout = useCallback(() => {
    clearTimeout(ref.current)
  }, [])

  // eslint-disable-next-line complexity
  const handleShowControls = useCallback(() => {
    if (isShowSubtitlesSettings || isShowListControl) {
      return
    }
    dispatch(changeShowControls(true))
    if (isForceShowControls) {
      return
    }
    handleClearTimeout()
    handleSetTimeout()
  }, [
    isShowSubtitlesSettings,
    isShowListControl,
    dispatch,
    isForceShowControls,
    handleClearTimeout,
    handleSetTimeout,
  ])

  const handleOpenSubtitleSettings = useCallback(() => {
    if (isShowControls) {
      handleClearTimeout()
      dispatch(changeShowControls(false))
    }

    dispatch(changeShowSubtitlesSettings(true))
  }, [isShowControls, dispatch, handleClearTimeout])

  const handleCloseSubtitleSettings = useCallback(() => {
    dispatch(changeShowSubtitlesSettings(false))

    if (isForceShowControls) {
      dispatch(changeShowControls(true))
    }
  }, [isForceShowControls, dispatch])

  const handleOpenChannelsList = useCallback(() => {
    if (isShowControls) {
      handleClearTimeout()
      dispatch(changeShowControls(false))
    }

    dispatch(changeShowTVListControl(true))
  }, [isShowControls, dispatch, handleClearTimeout])

  const handleCloseChannelsList = useCallback(() => {
    dispatch(changeShowTVListControl(false))
  }, [dispatch])

  const handleCallback = useCallback(() => {
    if (!isForceShowControls) handleShowControls()
  }, [isForceShowControls, handleShowControls])

  const handleKeyUpDown = useCallback(
    // eslint-disable-next-line complexity
    (event) => {
      if (isRecoveryFailed) return

      if (
        entity === 'CHANNEL' &&
        !isShowControls &&
        !isShowSubtitlesSettings &&
        (event?.code === 'ArrowLeft' || event?.code === 'ArrowRight')
      ) {
        handleOpenChannelsList()

        return
      }

      handleShowControls()
    },
    [
      entity,
      isRecoveryFailed,
      isShowControls,
      isShowSubtitlesSettings,
      handleShowControls,
      handleOpenChannelsList,
    ],
  )

  useEffect(() => {
    window?.removeEventListener('keydown', handleKeyUpDown)
    window?.removeEventListener('keyup', handleKeyUpDown)

    window?.addEventListener('keydown', handleKeyUpDown)
    window?.addEventListener('keyup', handleKeyUpDown)
    return () => {
      window?.removeEventListener('keydown', handleKeyUpDown)
      window?.removeEventListener('keyup', handleKeyUpDown)
    }
  }, [handleKeyUpDown])

  useEffect(() => {
    if (!isShowListControl) {
      handleShowControls()
    }
  }, [isShowListControl, handleShowControls])

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (isForceShowControls) {
      handleClearTimeout()
      if (!isShowSubtitlesSettings && !isShowControls && !isShowListControl) {
        dispatch(changeShowControls(true))
      }
    } else {
      handleShowControls()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isForceShowControls])

  useEffect(
    () => () => {
      handleClearTimeout()
    },
    [], // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleBackPress = useCallback(
    (event) => {
      if (isShowSubtitlesSettings) {
        handleCloseSubtitleSettings(false)
        event.stopPropagation()
      }
    },
    [handleCloseSubtitleSettings, isShowSubtitlesSettings],
  )

  useEffect(() => {
    window?.addEventListener(BUTTON_KEY_NAMES.BACK, handleBackPress, true)
    return () => window?.removeEventListener(BUTTON_KEY_NAMES.BACK, handleBackPress, true)
  }, [handleBackPress])

  return {
    isShowControls,
    isShowSubtitlesSettings,
    isShowListControl,
    onOpenSubtitleSettings: handleOpenSubtitleSettings,
    onCloseSubtitleSettings: handleCloseSubtitleSettings,
    onOpenChannelsList: handleOpenChannelsList,
    onCloseChannelsList: handleCloseChannelsList,
    onShowControls: handleCallback,
    onForceShowControls: handleForceShowControls,
  }
}

export default useUiControls
